import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Radio, CheckCircle2 } from "lucide-react";
import apiClient from "../../utils/apiClient";
import {
  AppCard, AppCardContent, AppCardHeader, AppCardTitle,
  AppButton, AppBadge, Callout, FormField, NativeSelect, ProgressBar,
} from "../ui";
import Shimmer from "../Common/Shimmer";
import CommonToaster from "../Common/CommonToaster";
import ZoomSettings from "./ZoomSettings";
import ZohoMeetingSettings from "./ZohoMeetingSettings";
import GratsMeetSettings from "./GratsMeetSettings";

/**
 * LiveClassSettings — the whole Live Class tab.
 *
 * One dropdown picks a provider and ONLY that provider's configuration renders
 * below it. It replaces three full-width provider cards stacked above all three
 * providers' settings blocks — a page on which an institute using Zoom scrolled
 * past two integrations it had not bought and could not use.
 *
 * ── Why the dropdown is a VIEW, not the switch itself ─────────────────────
 * Zoho and Grats Meet (in `self` mode) cannot be made active until an account
 * is connected, and the place to connect one is that provider's own settings
 * block. If picking it from the dropdown were the switch, reaching the connect
 * form would require a switch the server refuses — you could never get in.
 *
 * So the dropdown chooses what you are LOOKING AT, and a separate, explicit
 * "Use … for live classes" action switches. That also means a provider is never
 * switched by a stray click on a select: changing where live classes run is not
 * something to do by accident.
 *
 * ── Free trial ────────────────────────────────────────────────────────────
 * A trial's live classes run on Grats Meet and nothing else, with a budget of
 * hours checked whenever a class or demo starts (vc_be utils/trialMeetingPolicy).
 * So a trial sees no picker — only Grats Meet, the hours used, and the way out.
 * The server refuses the other providers too; hiding them here is not the lock.
 */

const PROVIDERS = [
  {
    key: "zoom",
    label: "Zoom",
    blurb:
      "Full webhook integration, embedded host SDK, and platform-hosted mode where the LMS supplies the meeting.",
  },
  {
    key: "zoho",
    label: "Zoho Meeting",
    blurb:
      "Uses your institute's own Zoho account. Recordings arrive by polling and the instructor joins in a new tab.",
  },
  {
    key: "grats",
    label: "Grats Meet",
    blurb:
      "Our own platform. The class runs inside the LMS with no meeting link to share, and recording starts on its own.",
  },
];

const LABELS = Object.fromEntries(PROVIDERS.map((p) => [p.key, p.label]));
const GRATS = PROVIDERS.find((p) => p.key === "grats");

/** "5 hours", "1 hour", "4 h 30 min", "45 min" — the server words its refusal the same way. */
function formatMinutes(minutes) {
  const total = Math.max(0, Math.round(minutes || 0));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h && !m) return `${h} hour${h === 1 ? "" : "s"}`;
  if (h) return `${h} h ${m} min`;
  return `${m} min`;
}

const LiveClassSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // What is live right now, and what the admin is looking at. They start equal.
  const [active, setActive] = useState("zoom");
  const [view, setView] = useState("zoom");
  const [zohoConnected, setZohoConnected] = useState(false);
  // Grats Meet is gated three ways — the server switch, the plan, and (in self
  // mode) a stored key. The settings endpoint answers all three, so this can say
  // WHICH one is missing instead of just refusing.
  const [grats, setGrats] = useState({ available: false, reason: "" });
  // Set only while a free trial locks live classes onto Grats Meet: the hours
  // allowed and used, as the server counts them (usage may be null if unread).
  const [trial, setTrial] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const showToast = useCallback(
    (message, type = "success") => setToast({ show: true, message, type }),
    [],
  );

  const load = useCallback(async () => {
    try {
      const [{ data }, gratsResult] = await Promise.all([
        apiClient.get("/institute/zoho-meeting-settings"),
        // Not fatal: an institute on a build without Grats Meet still gets its
        // Zoom/Zoho choice rather than an error screen.
        apiClient.get("/institute/grats-meet-settings").catch(() => null),
      ]);
      const provider = data.meetingProvider || "zoom";
      setActive(provider);
      setView(provider);
      setZohoConnected(!!data.connected);

      const g = gratsResult?.data;
      if (data.trialLocked || g?.trial?.locked) {
        setTrial({ ...(g?.trial || {}), locked: true });
        setActive("grats");
        setView("grats");
        setGrats({ available: true, reason: "" });
        return;
      }
      setTrial(null);
      if (!g || !g.serverEnabled) {
        setGrats({ available: false, reason: "Not available on this platform yet." });
      } else if (!g.allowedByPlan) {
        setGrats({ available: false, reason: "Not included in your plan — contact support to enable." });
      } else if (g.mode === "self" && !g.connected) {
        setGrats({ available: false, reason: "Add your Grats Meet API key below to enable." });
      } else {
        setGrats({ available: true, reason: "" });
      }
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to load provider settings.", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  /** Why this provider cannot be made active, or "" when it can. */
  const blockedReason = (key) => {
    if (key === "zoho" && !zohoConnected) return "Connect a Zoho Meeting account below first.";
    if (key === "grats" && !grats.available) return grats.reason;
    return "";
  };

  const handleSwitch = async () => {
    const reason = blockedReason(view);
    if (reason) return showToast(reason, "error");
    setSaving(true);
    try {
      await apiClient.put("/institute/meeting-provider", { provider: view });
      setActive(view);
      showToast(`Live classes now run on ${LABELS[view]}.`);
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to switch provider.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        <Shimmer height={140} />
        <Shimmer height={220} count={2} />
      </div>
    );
  }

  if (trial) {
    const allowance = trial.allowanceMinutes || 0;
    const known = trial.usedMinutes != null && allowance > 0;
    const pct = known ? Math.round((trial.usedMinutes / allowance) * 100) : 0;
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        <AppCard>
          <AppCardHeader>
            <AppCardTitle>
              <span className="tw-flex tw-items-center tw-gap-2">
                <Radio size={18} aria-hidden="true" /> Live Class Provider
              </span>
            </AppCardTitle>
            <p className="tw-m-0 tw-text-xs tw-text-content-muted">
              During your free trial, live classes and demos run on Grats Meet. Zoom and Zoho
              Meeting can be chosen after you upgrade.
            </p>
          </AppCardHeader>
          <AppCardContent className="tw-flex tw-flex-col tw-gap-3">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
              <span className="tw-text-sm tw-font-semibold tw-text-content">{GRATS.label}</span>
              <AppBadge variant="default">
                <CheckCircle2 size={12} aria-hidden="true" /> Active for live classes
              </AppBadge>
            </div>
            <p className="tw-m-0 tw-text-xs tw-text-content-muted">{GRATS.blurb}</p>

            {known ? (
              <div className="tw-flex tw-flex-col tw-gap-1.5">
                <div className="tw-flex tw-flex-wrap tw-items-baseline tw-justify-between tw-gap-2">
                  <span className="tw-text-sm tw-font-medium tw-text-content">Trial live-class time</span>
                  <span className="tw-text-sm tw-text-content-muted">
                    {formatMinutes(trial.usedMinutes)} of {formatMinutes(allowance)} used
                  </span>
                </div>
                <ProgressBar
                  value={pct}
                  color={trial.exhausted ? "danger" : pct >= 80 ? "warning" : "primary"}
                />
              </div>
            ) : null}

            {trial.exhausted ? (
              <Callout
                tone="warning"
                action={<AppButton size="sm" onClick={() => navigate("/billing")}>Upgrade</AppButton>}
              >
                Your trial&apos;s {formatMinutes(allowance)} of live classes are used, so a new class or
                demo cannot start. Upgrade your plan to keep teaching live.
              </Callout>
            ) : allowance > 0 ? (
              <p className="tw-m-0 tw-text-xs tw-text-content-muted">
                Your trial includes {formatMinutes(allowance)} of live classes in all — each class counts from
                when it starts to when it ends. A class that is running is never cut off; the time is
                checked when the next one starts.
              </p>
            ) : null}
          </AppCardContent>
        </AppCard>

        <GratsMeetSettings />
      </div>
    );
  }

  const current = PROVIDERS.find((p) => p.key === view) || PROVIDERS[0];
  const isActive = view === active;
  const reason = blockedReason(view);

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <AppCard>
        <AppCardHeader>
          {/* Tailwind rather than settings.scss's own card classes: this file
              imports no stylesheet, and a class borrowed from a sheet it does
              not load is what verify:split exists to catch. */}
          <AppCardTitle>
            <span className="tw-flex tw-items-center tw-gap-2">
              <Radio size={18} aria-hidden="true" /> Live Class Provider
            </span>
          </AppCardTitle>
          <p className="tw-m-0 tw-text-xs tw-text-content-muted">
            Pick a provider to configure it. New classes use whichever one is active; a class
            already running stays on the provider it started with.
          </p>
        </AppCardHeader>
        <AppCardContent className="tw-flex tw-flex-col tw-gap-3">
          <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3">
            <FormField label="Provider" className="tw-max-w-xs tw-flex-1">
              <NativeSelect value={view} onChange={(e) => setView(e.target.value)}>
                {PROVIDERS.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label}{p.key === active ? " — active" : ""}
                  </option>
                ))}
              </NativeSelect>
            </FormField>

            {isActive ? (
              <AppBadge variant="default" className="tw-mb-1">
                <CheckCircle2 size={12} aria-hidden="true" /> Active for live classes
              </AppBadge>
            ) : (
              <AppButton
                size="sm"
                className="tw-mb-1"
                onClick={handleSwitch}
                disabled={saving || !!reason}
              >
                {saving ? "Switching…" : `Use ${current.label} for live classes`}
              </AppButton>
            )}
          </div>

          <p className="tw-m-0 tw-text-xs tw-text-content-muted">{current.blurb}</p>

          {!isActive && (
            reason
              ? <Callout tone="warning">{reason} {LABELS[active]} keeps running live classes until then.</Callout>
              : <Callout tone="info">You are looking at {current.label}, but live classes still run on {LABELS[active]}.</Callout>
          )}
        </AppCardContent>
      </AppCard>

      {/* Only the provider being looked at. An institute on Zoom has no reason
          to scroll past two integrations it has not bought. */}
      {view === "zoom" && <ZoomSettings />}
      {view === "zoho" && <ZohoMeetingSettings />}
      {view === "grats" && <GratsMeetSettings />}

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default LiveClassSettings;
